import Loading from "vue-loading-overlay";
import MainLayout from "@/components/layout/admin/MainLayout.vue";
import { Bar } from "vue-chartjs";
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
} from "chart.js";
import store from "../../../store";
import axios from "axios";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
    name: "IncomeExpenseReport",
    components: {
        Loading,
        MainLayout,
        Bar,
        flatPickr
    },
    computed: {
        /**
         * Flatpickr config for both date pickers (no academic year restriction).
         */
        dateConfig() {
            return {
                dateFormat: "Y-m-d",
                altFormat: "d/m/Y",
                allowInput: false
            };
        }
    },
    data() {
        const chartDefaults = {
            responsive: true,
            maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
            },
              animation: {
                duration: 800,
                easing: 'easeOutQuart'
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: 'Categories',
                        font: {
                          weight: '600',
                          size: 14
                        },
                        color: '#4b5563'
                    },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 0,
                        color: '#374151',
                        font: {
                          size: 12,
                          weight: '500'
                      },
                        autoSkip: false,
                        callback: function(value) {
                        const label = this.getLabelForValue(value);
                        if (typeof label === 'string' && label.length > 15) {
                            return label.substring(0, 15) + '...';
                        }
                        return label;
                      }
                    },
                    grid: {
                        color: '#f3f4f6',
                        drawBorder: false
                    }
                },
                y: {
                    beginAtZero: true,
                    grace: '5%',
                    title: {
                        display: true,
                        text: 'Amount',
                        font: {
                          weight: '600',
                          size: 14
                        },
                        color: '#4b5563'
                    },
                    ticks: {
                        callback: (value) => value.toLocaleString(),
                        color: '#374151',
                        font: {
                            size: 12,
                            weight: '500'
                        }
                    },
                    grid: {
                        color: '#f3f4f6',
                        drawBorder: false
                    }
                },
            },
            plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                    backgroundColor: 'rgba(17, 24, 39, 0.95)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: 'transparent',
                    borderWidth: 0,
                    padding: 12,
                    cornerRadius: 8,
                    titleFont: {
                        weight: '600',
                        size: 14
                    },
                    bodyFont: {
                        size: 13
                    },
                    callbacks: {
                        label: function (context) {
                            return 'Amount: ' + context.parsed.y.toLocaleString();
                        }
                    }
                }
            },
        };

        return {
            isLoading: false,
            startDate: '',
            incomeChartData: {
                labels: [],
                datasets: []
            },
            expenseChartData: {
                labels: [],
                datasets: []
            },
            chartOptions: chartDefaults,
            totalIncome: 0,
            totalExpense: 0,
            currency: (store.state.setting && store.state.setting.currency) || 'MMK',
        };
    },

    methods: {
        /**
         * Match Profit/Loss Report card format: rounded amount + currency.
         * (Profit/Loss uses PHP number_format($amount) . ' ' . currency)
         */
        formatTotalAmount(value) {
            const currency = this.currency || (store.state.setting && store.state.setting.currency) || 'MMK';
            return Math.round(Number(value) || 0).toLocaleString('en-US') + ' ' + currency;
        },

        onStartDateChange() {
            this.getIncomeExpenseCategoryReport();
        },

        getIncomeExpenseCategoryReport() {
            this.isLoading = true;
            const request = {
              params: {
                  start_date: this.startDate,
                  end_date: new Date().toISOString().split('T')[0],
              },
            };

            axios
                .get("/report/income-expense-category", request)
                .then((response) => {
                    const incomeData = response.data.income_data;
                    const expenseData = response.data.expense_data;
                    const ensureRgba = (color) => {
                        if (!color) return 'rgba(158, 158, 158, 0.9)';
                        if (color.startsWith('#')) {
                          let r = 0, g = 0, b = 0;
                          if (color.length === 4) {
                              r = parseInt(color[1] + color[1], 16);
                              g = parseInt(color[2] + color[2], 16);
                              b = parseInt(color[3] + color[3], 16);
                          } else if (color.length === 7) {
                              r = parseInt(color[1] + color[2], 16);
                              g = parseInt(color[3] + color[4], 16);
                              b = parseInt(color[5] + color[6], 16);
                          }
                          return `rgba(${r}, ${g}, ${b}, 0.9)`;
                        }
                        return color;
                    };

                    const getSolidColor = (color) => {
                      return color.replace('0.9', '1');
                    };

                    this.incomeChartData = {
                        labels: response.data.income_labels || [],
                        datasets: incomeData ? [{
                            label: 'Income',
                            data: incomeData.data.map(n => Number(n)),
                            backgroundColor: incomeData.backgroundColor.map(ensureRgba),
                            borderColor: incomeData.backgroundColor.map(c => getSolidColor(ensureRgba(c))),
                            borderWidth: 2,
                            borderRadius: 6,
                            maxBarThickness: 60,
                            minBarLength: 8,
                        }] : []
                    };

                    this.expenseChartData = {
                        labels: response.data.expense_labels || [],
                        datasets: expenseData ? [{
                            label: 'Expense',
                            data: expenseData.data.map(n => Number(n)),
                            backgroundColor: expenseData.backgroundColor.map(ensureRgba),
                            borderColor: expenseData.backgroundColor.map(c => getSolidColor(ensureRgba(c))),
                            borderWidth: 2,
                            borderRadius: 6,
                            maxBarThickness: 60,
                            minBarLength: 8,
                        }] : []
                    };

                    this.totalIncome = this.incomeChartData.datasets[0]?.data.reduce((a, b) => a + b, 0) || 0;
                    this.totalExpense = this.expenseChartData.datasets[0]?.data.reduce((a, b) => a + b, 0) || 0;
                    this.currency = response.data.currency
                        || (store.state.setting && store.state.setting.currency)
                        || 'MMK';

                    this.isLoading = false;
                })
                .catch((error) => {
                    if (error.response && error.response.status == 401) {
                        this.$store.commit("logout");
                        window.location.replace("login");
                    }
                    this.isLoading = false;
                    console.log("Income/Expense category report API error", error);
                });
        },

        getInitialStartDate() {
            axios
                .get("/period/getCurrentPeriodList", {
                    params: {
                        campus_id: store.state.user.campus_id,
                    },
                })
                .then(res => {
                    if (res.data.length == 1) {
                        this.startDate = res.data[0].start_date;
                    } else {
                        const now = new Date();
                        this.startDate = `${now.getFullYear()}-06-01`;
                    }
                    this.getIncomeExpenseCategoryReport();
                })
                .catch(err => {
                    const now = new Date();
                    this.startDate = `${now.getFullYear()}-06-01`;
                    this.getIncomeExpenseCategoryReport();
                    console.error("Error getting initial start date:", err);
                });
        },
    },

    mounted() {
        require("@/assets/plugins/css/select2.css");
        require("@/assets/css/report.css");
        this.isLoading = true;
        this.getInitialStartDate();
    },
};
